import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  api,
  onApiSuccess,
  onWorkspaceReseated,
  onWorkspaceMismatch,
  setActiveWorkspaceId,
  getActiveWorkspaceId,
  isRetryableError,
  referenceCode,
} from "@/lib/api";
import { clearAllBrandDrafts } from "@/lib/brandDraft";
import {
  getBrandPref,
  setBrandPref,
  clearBrandPref,
  clearAllBrandPrefs,
  resolveBrand,
  getBrandParam,
} from "@/lib/brandPref";

/**
 * WORKSPACE STATUS — the whole point of this file.
 *
 * The old `refreshWorkspace` swallowed every failure into `setWorkspace(null)`
 * and never retried. There was no status field, so no consumer downstream could
 * tell "not fetched yet" from "fetched and failed" — `useEntitlements` collapsed
 * both into `loaded: false`. A 500 on /workspaces/current therefore stranded the
 * app forever on "Loading workspace…", and it did not recover even after the
 * backend came back. Only a manual reload escaped. That single missing bit of
 * state was the entire defect.
 *
 * The pending vocabulary already built (PendingButton, billing-usage-pending,
 * scan-pending-btn) is correct and honest for the first case and A LIE for the
 * second. So: a new status, not new components.
 *
 *   settling    in flight, < 600ms      → nothing on screen
 *   loading     in flight, > 600ms      → "Checking…"      — true
 *   ready       got it                  → —
 *   retrying    retry in flight/queued  → "Trying again"   — true
 *   unreachable ladder exhausted        → "Couldn't load"  — true
 *
 * Consumers branch on `workspaceStatus`, NEVER on `workspace === null`.
 *
 * ── TENANT EPOCH (Phase 5.3, widened to brands in B2) ───────────────────────
 *
 * `tenantEpoch` is a monotonic counter that increments every time the seated
 * TENANT changes identity. "Tenant" here is the pair (workspace, brand), not
 * the workspace alone: a switch, a reseat, a 409 re-sync — or a brand change
 * inside one workspace. It is the thing that replaces `window.location.assign`
 * in WorkspaceSwitcher.
 *
 * ONE counter, not two. Every consumer of the old `workspaceEpoch` wants
 * identical behaviour when the brand changes — drop tenant-derived state,
 * refetch, kill in-flight polls — because almost everything on screen is
 * brand-scoped, not merely workspace-scoped. Two counters would mean every one
 * of those call sites combining them by hand, and each combination is a chance
 * to combine them wrong in a way that fails silently and looks fine in review.
 * `workspaceEpoch` remains exported as an alias so nothing breaks mid-migration.
 *
 * The reload was load-bearing for one reason: roughly a dozen screens fetch
 * workspace-scoped data in a mount-time `useEffect` with `[]` deps. Nothing told
 * them the tenant had changed, so nothing refetched, so they kept rendering
 * workspace A's numbers under workspace B's name. A full page reload remounted
 * everything and made that structurally impossible.
 *
 * The epoch does the same job without throwing the app away:
 *
 *   · every tenant-scoped effect lists `tenantEpoch` in its dep array, so a
 *     switch re-runs it exactly as a remount would;
 *   · every poll loop CAPTURES the epoch when it starts and bails the moment it
 *     no longer matches, so A's 8-minute scan poll cannot deliver into B;
 *   · every `await`-then-`setState` re-checks the epoch AFTER the await and
 *     drops the write on a mismatch. A mount-cancel flag is NOT a substitute:
 *     it never fires when the component stays mounted and only the brand
 *     changed underneath it;
 *   · every mutation that acts on a list fetched at mount compares the epoch it
 *     was fetched under against the current one, and refuses on a mismatch.
 *
 * The bump is SYNCHRONOUS with the assignment (`seatWorkspace` / `seatBrand` /
 * `switchBrand`), not an effect watching `workspace` or `brand`. That matters:
 * child effects run before parent effects, so a bump-in-an-effect would give
 * consumers one render holding the NEW identity and the OLD epoch — during
 * which they would fetch, under the old identity, and race their own
 * correction. The trap applies verbatim to brand.
 *
 * Fail closed while it moves. A workspace switch clears the seat
 * (`workspace = null`, status back to `settling`) before re-deriving, and a
 * brand switch clears `brand` to null AND bumps in the same commit before
 * fetching the new one — so between the two tenants there is no tenant at all,
 * not even for a frame, and nothing can render the previous one's numbers.
 * That reuses the pending vocabulary already built rather than inventing a
 * second one.
 */
export const WS_STATUS = {
  SETTLING: "settling",
  LOADING: "loading",
  READY: "ready",
  RETRYING: "retrying",
  UNREACHABLE: "unreachable",
};

// Spec §2. t=0 at first GET; F = first failure.
const SETTLE_MS = 600;      // below this, nothing registers — a spinner here
                            // manufactures a wait that didn't exist.
const HONESTY_MS = 3000;    // F+3.0s. THE line. Before it we are honestly
                            // loading and may say so. After it, "Loading…" is a
                            // lie. Everything else in this file is craft; this
                            // timestamp is the law.
const S3_MIN_DWELL_MS = 1200; // a 200ms banner is a scare, not information.
const RETRY_DELAYS = [400, 1500, 4000, 8000]; // 4 retries after the initial
                                              // attempt = 5 attempts, ≈17s.
const JITTER = 0.25;
const MAX_ATTEMPTS = RETRY_DELAYS.length + 1;

const HEAL_POLL_MS = 20000;       // §3.1
const HEAL_POLL_SLOW_MS = 60000;  // backs off after 5 min
const HEAL_BACKOFF_AFTER_MS = 5 * 60 * 1000;
const HIDDEN_POLL_CAP_MS = 5 * 60 * 1000; // owner decision 3: don't burn mobile
                                          // data on a backgrounded tab.
const NUDGE_THROTTLE_MS = 3000;
const ROUTE_NUDGE_THROTTLE_MS = 5000; // §3.4
const HEALED_DWELL_MS = 2500;         // S5 — exactly.

const jitter = (ms) => Math.round(ms * (1 + (Math.random() * 2 - 1) * JITTER));

const AuthCtx = createContext({
  user: null,
  workspace: null,
  brand: null,
  brands: [],
  brandLoading: false,
  brandGone: null,
  clearBrandGone: () => {},
  reportBrandGone: () => {},
  loading: true,
  workspaceStatus: WS_STATUS.SETTLING,
  tenantEpoch: 0,
  workspaceEpoch: 0, // alias of tenantEpoch — see the header note
  wsSwitching: null,
  brandSwitching: null,
  wsAttempt: 0,
  wsRequestOpen: false,
  wsNextRetryAt: null,
  wsRefCode: null,
  wsOffline: false,
  wsLaddersExhausted: 0,
  wsHealing: false,
  watchStatus: "polling",
  retryWorkspace: () => {},
  healNudge: () => {},
  refreshAuth: async () => {},
  refreshWorkspace: async () => {},
  refreshBrand: async () => {},
  switchWorkspace: async () => {},
  switchBrand: async () => {},
  logout: async () => {},
  setUser: () => {},
  setWorkspace: () => {},
});

export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [brand, setBrand] = useState(null);
  // The workspace's brands, fetched ONCE by `refreshBrand` and shared. The
  // switcher needs the list to know whether to render at all (a switcher that
  // cannot switch is noise), and a second `/brands` fetch there would be a
  // second answer to "which brands exist" that can disagree with this one —
  // exactly the parallel-copy problem the epoch work spent its time deleting.
  const [brands, setBrands] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  // Set when a brand-scoped read 404s: the brand is gone (deleted here, in
  // another tab, or by /brands/reset). Holds `{ brandId, name }` for the notice
  // and is cleared by the surface that shows it. NOT a toast from this file —
  // AuthContext never words anything.
  const [brandGone, setBrandGone] = useState(null);
  const [loading, setLoading] = useState(true);

  const [workspaceStatus, setWorkspaceStatus] = useState(WS_STATUS.SETTLING);
  const [tenantEpoch, setTenantEpoch] = useState(0);
  // The workspace_id a switch is currently travelling TO, or null. Consumers use
  // it to render a transition rather than a stale tenant.
  const [wsSwitching, setWsSwitching] = useState(null);
  // Same idea, one level down: the brand_id a brand switch is travelling to.
  const [brandSwitching, setBrandSwitching] = useState(null);
  const [wsAttempt, setWsAttempt] = useState(0);
  // `wsRequestOpen` exists for exactly one reason: `ws.retry.attempt` says
  // "Trying again NOW". That word is load-bearing, and the string may only
  // render while a request is genuinely open — never while one is merely
  // scheduled. Honesty condition 2.
  const [wsRequestOpen, setWsRequestOpen] = useState(false);
  const [wsNextRetryAt, setWsNextRetryAt] = useState(null);
  const [wsRefCode, setWsRefCode] = useState(null);
  const [wsOffline, setWsOffline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine === false : false
  );
  const [wsLaddersExhausted, setWsLaddersExhausted] = useState(0);
  const [wsHealing, setWsHealing] = useState(false);
  // `watchStatus` exists for exactly one reason: `ws.down.watching` claims
  // "We're still checking in the background." Owner decision 3 stops that poll
  // after ~5 min on a hidden tab, at which point the line is false, silently,
  // on a trigger nobody can see. Hiding it is not enough — the user's last
  // information would remain "they're watching for me". So we RETRACT it with
  // ws.down.watching.paused. Honesty condition 1.
  const [watchStatus, setWatchStatus] = useState("polling");

  // ── Ladder bookkeeping (refs: mutated from timers, never rendered raw) ──
  const genRef = useRef(0);          // cancels superseded ladders
  const timersRef = useRef([]);
  const statusRef = useRef(WS_STATUS.SETTLING);
  const announcedRef = useRef(false); // did the user actually SEE S3?
  const s3AtRef = useRef(0);          // for the 1200ms min-dwell
  const unreachableAtRef = useRef(0);
  const lastNudgeRef = useRef(0);
  const lastRouteNudgeRef = useRef(0);
  const hiddenSinceRef = useRef(0);
  const authFailedRef = useRef(false);
  // The last workspace id we were actually seated in. Deliberately NOT cleared
  // when the workspace goes null: a failed ladder mid-switch would otherwise
  // erase the A→B transition and the epoch would never bump, leaving every
  // consumer holding A's data with B seated. Only a real id overwrites it.
  const lastRealWsIdRef = useRef(null);
  // The last brand id we were actually seated on, under the CURRENT workspace.
  // Same "only a real id overwrites it" rule as above and for the same reason:
  // a failed /brands fetch momentarily nulls `brand`, and if that erased the
  // memory, the identical brand landing on the retry would look like a change
  // and bump the epoch — a spurious full refetch of every screen.
  const lastRealBrandIdRef = useRef(null);

  const bumpTenantEpoch = useCallback(() => setTenantEpoch((n) => n + 1), []);

  /**
   * Retire the stored brand preference — but ONLY if it is the brand that just
   * went away. Clearing unconditionally would throw away a perfectly good
   * preference for a DIFFERENT brand every time some other brand 404s, and the
   * user would silently lose their selection on a reload for a reason that had
   * nothing to do with them.
   */
  const forgetBrandPrefIf = useCallback((brandId) => {
    const wsId = getActiveWorkspaceId();
    if (brandId && getBrandPref(wsId) === brandId) clearBrandPref(wsId);
  }, []);

  const setStatus = useCallback((s) => {
    statusRef.current = s;
    setWorkspaceStatus(s);
  }, []);

  /**
   * THE single place `workspace` is assigned. Everything that changes which
   * tenant we are in goes through here so three things can never drift apart:
   *
   *   1. `workspace`            — what the UI renders
   *   2. the `X-Workspace-Id`   — what every request claims (lib/api.js)
   *   3. `tenantEpoch`          — what tells effects and polls to start over
   *
   * (2) is module state, so it is correct the instant this returns — before any
   * request the resulting render could fire. (1) and (3) are batched into the
   * same render by React 18, so no consumer ever observes the new workspace
   * under the old epoch.
   */
  const seatWorkspace = useCallback((ws) => {
    const id = ws?.workspace_id || null;
    setActiveWorkspaceId(id);
    setWorkspace(ws);
    if (id) {
      const prev = lastRealWsIdRef.current;
      lastRealWsIdRef.current = id;
      if (prev && prev !== id) {
        // A brand id is only meaningful inside its workspace. Forgetting it
        // here is what keeps a workspace switch to exactly ONE bump: the new
        // workspace's brand then lands against `prev === null` and does not
        // bump a second time.
        lastRealBrandIdRef.current = null;
        bumpTenantEpoch();
      }
    }
  }, [bumpTenantEpoch]);

  /**
   * THE single place `brand` is assigned. Mirrors `seatWorkspace`, and bumps
   * the SAME counter, synchronously, for the same reason.
   *
   * Seating `null` deliberately does not bump and does not forget the id: a
   * transient clear (a failed refresh, a mid-switch window) is not a change of
   * identity. Only landing on a DIFFERENT real brand is.
   */
  const seatBrand = useCallback((b) => {
    const id = b?.brand_id || null;
    setBrand(b);
    if (!id) return;
    const prev = lastRealBrandIdRef.current;
    lastRealBrandIdRef.current = id;
    if (prev && prev !== id) bumpTenantEpoch();
  }, [bumpTenantEpoch]);

  /**
   * Drop the brand AND the memory of it. For the paths where the workspace
   * itself is going away or already gone — there, the old brand id is not a
   * "previous value to compare against", it is a fact about a different tenant.
   * Called before re-deriving so the incoming brand cannot be mistaken for a
   * change and double-bump an epoch the workspace switch already bumped.
   */
  const clearBrandSeat = useCallback(() => {
    setBrand(null);
    // The LIST goes too. It is as tenant-derived as the seated brand, and the
    // switcher renders directly off it — leaving it would show the workspace
    // being left's brand names in the menu of the workspace being entered, for
    // however long `/brands` takes. Fail closed: no brands until we know.
    setBrands([]);
    setBrandGone(null);
    lastRealBrandIdRef.current = null;
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  /**
   * Run one full ladder: up to 5 attempts at 400ms / 1.5s / 4s / 8s (±25%
   * jitter). Resolves by setting status to ready or unreachable.
   *
   * `announce` timing is what makes this honest:
   *   - t=0..600ms          settling  (nothing on screen)
   *   - 600ms..F            loading   (legitimately loading)
   *   - F..F+3.0s           STILL loading — the danger window. Most blips
   *                         resolve inside it, and an alarm that retracts
   *                         itself trains people to ignore alarms.
   *   - F+3.0s              retrying  — we say so, out loud.
   */
  const runLadder = useCallback(
    async (opts = {}) => {
      const gen = ++genRef.current;
      clearTimers();
      const alive = () => genRef.current === gen;

      const fresh = opts.fresh !== false;
      if (fresh) {
        announcedRef.current = false;
        authFailedRef.current = false;
      }

      // S3 may be entered immediately, not at F+3s, when we already KNOW the
      // network is down — there is no blip to wait out. (§2, S3 trigger.)
      const offlineNow = typeof navigator !== "undefined" && navigator.onLine === false;
      setWsOffline(offlineNow);

      // Only start the settle→loading clock if we're not already showing
      // something louder. Re-entering from a heal must not flash "Checking…".
      if (statusRef.current === WS_STATUS.SETTLING || fresh) {
        if (!opts.keepStatus) {
          setStatus(WS_STATUS.SETTLING);
          later(() => {
            if (alive() && statusRef.current === WS_STATUS.SETTLING) {
              setStatus(WS_STATUS.LOADING);
            }
          }, SETTLE_MS);
        }
      }

      const announce = () => {
        if (!alive() || announcedRef.current) return;
        announcedRef.current = true;
        s3AtRef.current = Date.now();
        setStatus(WS_STATUS.RETRYING);
      };

      if (offlineNow || opts.announceNow) announce();

      let honestyTimer = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        if (!alive()) return;
        setWsAttempt(attempt);
        setWsNextRetryAt(null);
        setWsRequestOpen(true);
        try {
          const ws = await api.get("/workspaces/current").then((r) => r.data);
          if (!alive()) return;
          setWsRequestOpen(false);
          seatWorkspace(ws);
          setWsRefCode(null);
          // via ref: runLadder is memoised before finishHealthy exists, and we
          // must not capture a stale copy of it.
          await finishHealthyRef.current(gen);
          return ws;
        } catch (e) {
          if (!alive()) return;
          setWsRequestOpen(false);

          // A 401/403 is not a retry case — ProtectedRoute owns that, and the
          // banner must stay out of its way. Bail immediately.
          if (!isRetryableError(e)) {
            authFailedRef.current = true;
            clearTimers();
            seatWorkspace(null);
            setStatus(WS_STATUS.UNREACHABLE);
            return null;
          }

          setWsRefCode(referenceCode(e));
          // eslint-disable-next-line no-console
          console.error("[workspace] fetch failed", {
            attempt,
            status: e?.response?.status,
            detail: e?.response?.data?.detail,
            ref: referenceCode(e),
          });

          // F — start the 3s honesty clock once, on the FIRST failure.
          if (honestyTimer === null && !announcedRef.current) {
            honestyTimer = later(announce, HONESTY_MS);
          }

          if (attempt === MAX_ATTEMPTS) break;

          const delay = jitter(RETRY_DELAYS[attempt - 1]);
          setWsNextRetryAt(Date.now() + delay);
          await new Promise((res) => later(res, delay));
        }
      }

      if (!alive()) return null;
      clearTimers();
      setWsNextRetryAt(null);
      setWsRequestOpen(false);
      seatWorkspace(null);
      // If the tab was hidden for the whole ladder, do NOT show S4 cold on
      // return — §3.2. We record the moment and the visibility handler
      // restarts the ladder at S3 rather than letting S4 greet them.
      announcedRef.current = true;
      unreachableAtRef.current = Date.now();
      setWsLaddersExhausted((n) => n + 1);
      setWatchStatus("polling");
      setStatus(WS_STATUS.UNREACHABLE);
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearTimers, later, setStatus, seatWorkspace]
  );

  /**
   * Success path. S5 "Back online" runs for exactly 2.5s — but ONLY if the user
   * ever saw S3. If we healed during the quiet window there is nothing to
   * un-say, and announcing a recovery from a failure they never saw invents a
   * problem retroactively (§3, exception).
   *
   * Also honours the S3 min-dwell: if the banner went up 300ms ago we hold it
   * to 1200ms first, so it reads as information rather than a flicker.
   */
  const finishHealthy = useCallback(
    async (gen) => {
      const alive = () => genRef.current === gen;
      // announcedRef is set ONLY by announce() (which raises S3) or by ladder
      // exhaustion (S4). If it is false the user never saw a failure, and there
      // is nothing to un-say — heal silently.
      const sawBanner = announcedRef.current;
      setWsAttempt(0);
      setWsNextRetryAt(null);
      setWatchStatus("polling");
      setWsLaddersExhausted(0);

      if (!sawBanner) {
        clearTimers();
        setStatus(WS_STATUS.READY);
        return;
      }

      const shown = Date.now() - (s3AtRef.current || Date.now());
      if (shown < S3_MIN_DWELL_MS) {
        await new Promise((res) => later(res, S3_MIN_DWELL_MS - shown));
        if (!alive()) return;
      }
      setWsHealing(true);
      setStatus(WS_STATUS.READY);
      later(() => {
        if (alive()) setWsHealing(false);
        announcedRef.current = false;
      }, HEALED_DWELL_MS);
    },
    [clearTimers, later, setStatus]
  );
  const finishHealthyRef = useRef(finishHealthy);
  finishHealthyRef.current = finishHealthy;

  const refreshWorkspace = useCallback(
    (opts) => runLadder(opts),
    [runLadder]
  );

  /**
   * Every self-heal trigger lands here, and every one of them resets the ladder
   * to attempt 1. Reload-as-only-escape is the filmed bug; this function is the
   * fix. It is deliberately cheap to call — callers throttle, we de-dupe.
   */
  const healNudge = useCallback(
    (reason) => {
      const s = statusRef.current;
      if (s !== WS_STATUS.UNREACHABLE && s !== WS_STATUS.RETRYING) return;
      if (authFailedRef.current) return; // not our problem to fix
      // Coming back from S4 must not flash "Checking…" — keep the banner up
      // and restart the ladder announced (S3), per §3.2.
      const announceNow = s === WS_STATUS.UNREACHABLE || announcedRef.current;
      // eslint-disable-next-line no-console
      console.info("[workspace] heal nudge:", reason);
      runLadder({ fresh: false, keepStatus: true, announceNow });
    },
    [runLadder]
  );

  const retryWorkspace = useCallback(() => {
    if (authFailedRef.current) return;
    runLadder({ fresh: false, keepStatus: true, announceNow: true });
  }, [runLadder]);

  /**
   * Re-derive the seated brand from the server. THE one place the "which brand
   * is mine" rule lives — `Dashboard`, `BrandFoundation` and `Onboarding` each
   * re-derived it independently, and one of them diverged, which is how two
   * different brands ended up on screen at once.
   *
   * The order matters and is not the old `find(confirmed) || list[0]`:
   *
   *   1. the brand we are ALREADY seated on, if it still exists. Without this,
   *      any refresh (a workspace re-sync, a heal) would silently yank a user
   *      off the brand they picked and back onto the default. Selection is
   *      state, and a refresh must not be a state change.
   *   2. the STORED PREFERENCE for this workspace — the brand they last chose.
   *      This is what makes selection survive a reload. Before it, (1) was
   *      empty on a cold mount and every refresh landed on the default, so
   *      picking client B and pressing F5 put client A's numbers on screen.
   *      Honoured only if the id is still in the list; a preference naming a
   *      deleted brand is dropped here rather than seated and left to 404.
   *   3. the first confirmed brand — the onboarding default.
   *   4. `list[0]`. Backend sorts oldest-first, so this is deterministic now.
   *      It can still promote an UNCONFIRMED draft to "the brand"; that is a
   *      separate defect (a draft brand_id is scannable) and is not fixed here.
   *
   * (2)-(4) live in `resolveBrand` (lib/brandPref.js) so the add-brand flow and
   * the URL-param sync resolve identically rather than each re-deriving it —
   * four independent copies of this rule, one of which had diverged, is the
   * defect this whole file exists to have stopped repeating.
   *
   * The preference is read under `getActiveWorkspaceId()`, NOT the seated
   * `workspace` object. Mid-switch those disagree: `switchWorkspace` clears the
   * seat and pins the target header before calling this, so the seated value is
   * briefly the workspace we are LEAVING. The header id is what the `/brands`
   * request is actually scoped to, so keying the preference off it means the
   * list and the preference can never be about different workspaces.
   */
  const refreshBrand = useCallback(async () => {
    setBrandLoading(true);
    try {
      const list = await api.get("/brands").then((r) => r.data?.items || []);
      setBrands(list);
      const wsId = getActiveWorkspaceId();
      const seatedId = lastRealBrandIdRef.current;
      const seated = seatedId ? list.find((b) => b.brand_id === seatedId) : null;
      const preferredId = getBrandPref(wsId);
      // A preference that names nothing is worse than none: it loses every
      // resolution from here on and re-loses it on every load. This is the only
      // place that sees both the preference and the authoritative list, so it
      // is the only place that can retire it.
      if (preferredId && !list.some((b) => b.brand_id === preferredId)) {
        clearBrandPref(wsId);
      }
      // URL param first, stored preference second, then the deterministic
      // default. Reading the param HERE — before anything is seated — is what
      // keeps a cold load of `?brand=B` from rendering brand A first.
      const fallback = resolveBrand(list, getBrandParam(), preferredId);

      if (seatedId && !seated) {
        // The brand we were seated on no longer exists — `DELETE /brands/{id}`
        // or `POST /brands/reset`. It is not a "previous value to compare
        // against" any more, so forget it outright; keeping it would make the
        // NEXT brand created look like a change and bump spuriously.
        lastRealBrandIdRef.current = null;
        if (fallback) {
          // Deleted, and something else took its place. That IS a tenant change
          // — everything brand-derived on screen belongs to a brand that is
          // gone — so bump explicitly, since `seatBrand` now sees no previous.
          seatBrand(fallback);
          bumpTenantEpoch();
          return fallback;
        }
        // Wiped entirely. Deliberately NO bump: the workspace has no brand at
        // all, brand-keyed effects already re-run on `brand` going null, and a
        // bump here would fire the tenant-reset effects — including the one in
        // Onboarding, which is exactly where `/brands/reset` sends the user.
        // Wiping their in-progress onboarding would be the cure causing the
        // disease.
        setBrand(null);
        return null;
      }

      seatBrand(seated || fallback);
      return seated || fallback;
    } catch (_) {
      // Seat null WITHOUT forgetting the id — a failed fetch is not a change of
      // brand, and the retry that returns the same brand must not bump.
      setBrand(null);
      return null;
    } finally {
      setBrandLoading(false);
    }
  }, [seatBrand, bumpTenantEpoch]);

  /**
   * Change brand inside the current workspace. The brand-level twin of
   * `switchWorkspace`, and the reason `tenantEpoch` exists rather than
   * `workspaceEpoch`.
   *
   * There is no server-side "active brand" — selection is client state — so the
   * ordering argument is different from the workspace one and worth stating:
   *
   *   1. Drop the old brand and bump IN THE SAME COMMIT. React 18 batches them,
   *      so consumers observe `brand === null` and the new epoch in one render.
   *      There is no frame in which A's results sit under B's identity, and no
   *      frame in which a consumer sees the new epoch while still holding A.
   *      This is the whole "fail closed through the transition" rule.
   *   2. Claim the target id in the ref at the same moment. That is what makes
   *      the landing in (3) a no-op for the counter: one switch, one bump.
   *   3. Fetch the target THROUGH the server, scoped by workspace. `GET
   *      /brands/{id}` 404s for a brand in another workspace, so a stale or
   *      guessed id fails loudly instead of seating something cross-tenant.
   *   4. On failure, disown the claim and fall back to derivation, then bump
   *      again — the user is not on the brand they asked for, and everything
   *      downstream needs to hear about whatever they ARE on.
   *
   * `remember` records the pick as this workspace's stored preference, so it
   * survives a reload. It defaults to TRUE because every caller that reaches
   * here through a human — the switcher, the add-brand confirm — is by
   * definition an explicit choice. The one caller that passes `false` is the
   * URL-param sync: there the tab's own `?brand=` already carries the selection,
   * and writing it to the shared preference would let one tab silently retarget
   * the OTHER tab's next cold load, which is the side-by-side case breaking
   * itself. See lib/brandPref.js.
   *
   * Errors are re-thrown for the caller to word — this file never toasts.
   */
  const switchBrand = useCallback(
    async (brandId, { remember = true } = {}) => {
      if (!brandId) return null;
      if (brandId === lastRealBrandIdRef.current) {
        // Already seated, but the CHOICE may still be new — picking the brand
        // you are already on is how a user makes an accidental default stick.
        if (remember) setBrandPref(getActiveWorkspaceId(), brandId);
        return brandId;
      }
      setBrandSwitching(brandId);
      setBrandLoading(true);
      setBrand(null);                        // (1) fail closed…
      lastRealBrandIdRef.current = brandId;  // (2) …claim the target…
      bumpTenantEpoch();                     // …all in one commit.
      try {
        const next = await api
          .get(`/brands/${brandId}`)
          .then((r) => r.data?.brand || null);
        if (!next?.brand_id) throw new Error("Brand not found");
        seatBrand(next); // prev === brandId, so no second bump
        // Written only AFTER the server confirmed the brand exists and is ours.
        // Storing the id before the fetch would persist a preference for a
        // brand that may 404 — the stale-preference loop, created by the very
        // code meant to avoid it.
        if (remember) setBrandPref(getActiveWorkspaceId(), brandId);
        return brandId;
      } catch (e) {
        lastRealBrandIdRef.current = null;
        // A 404 here is not a generic failure: it means the brand we were asked
        // to seat does not exist for this workspace (deleted in another tab, or
        // an id from somewhere it should not have come from). Retire the
        // preference so the next load does not repeat the same dead lookup.
        if (e?.response?.status === 404) forgetBrandPrefIf(brandId);
        await refreshBrand();
        bumpTenantEpoch();
        throw e;
      } finally {
        setBrandSwitching(null);
        setBrandLoading(false);
      }
    },
    [seatBrand, refreshBrand, bumpTenantEpoch, forgetBrandPrefIf]
  );

  /**
   * "The brand this screen is about no longer exists." B6.
   *
   * The brand-level analogue of `X-Workspace-Reseated`, and it exists for the
   * reason auth.py argues at length about that header: healing SILENTLY is its
   * own defect. A brand deleted in another tab leaves this tab polling a 404,
   * and the two tempting responses are both wrong — keep going (a screen full
   * of a deleted brand's numbers, refreshing forever) or quietly re-derive (the
   * heading changes, every number changes, and nothing says why; the user
   * concludes the app lost their data).
   *
   * So: clear it, re-derive, and SAY SO. This sets the fact; a visible surface
   * words it and clears it.
   *
   * Deliberately NOT an axios interceptor, unlike the 409. The interceptor sees
   * every response but cannot tell WHICH 404 means "brand gone" — `/leads/{id}`,
   * `/scans/{id}` and a mistyped path all 404 identically. Only the caller knows
   * it was reading a brand, and which one. Guarded on the id so a late 404 for a
   * brand the user already left cannot evict the one they are on now.
   */
  const reportBrandGone = useCallback(
    async (brandId, { name = null } = {}) => {
      if (!brandId) return;
      if (brandId !== lastRealBrandIdRef.current) {
        // Gone, but it was never the seated brand — an id from a URL param or a
        // link that names a brand this workspace does not have. There is no
        // seat to clear and nothing on screen belongs to it, so re-deriving
        // would be a pointless churn of every brand-scoped effect. Still SAY
        // it: the user asked for a specific brand and did not get it, and
        // silently showing them a different one is the "healed quietly" defect.
        forgetBrandPrefIf(brandId);
        setBrandGone({ brandId, name });
        return;
      }
      const goneName = name || (brand?.brand_id === brandId ? brand?.name : null);
      // eslint-disable-next-line no-console
      console.warn("[brand] gone —", brandId, "— re-deriving");
      forgetBrandPrefIf(brandId);
      lastRealBrandIdRef.current = null;
      setBrand(null);
      setBrandGone({ brandId, name: goneName });
      // Re-derive from the server. `refreshBrand` seats whatever is left (or
      // null for an emptied workspace) and bumps, which re-runs every
      // brand-scoped effect against the brand the user actually has now.
      await refreshBrand();
      bumpTenantEpoch();
    },
    [brand, refreshBrand, bumpTenantEpoch, forgetBrandPrefIf]
  );

  const clearBrandGone = useCallback(() => setBrandGone(null), []);

  /**
   * Change tenant. THE replacement for `window.location.assign` (Phase 5.9).
   *
   * The order below is the whole safety argument, so it is worth stating:
   *
   *   1. POST first, change nothing locally. A switch that the server refuses
   *      (403 — not a member) must leave the client exactly as it was; there is
   *      no half-switched state to unwind.
   *   2. Only once it is committed, DROP the old tenant: clear the brand, clear
   *      the seat, drop the status back to `settling`. From this instant there
   *      is no workspace at all, so nothing on screen can render A's numbers
   *      under B's name — the failure the reload existed to prevent. This is the
   *      fail-closed window, and it is deliberately a window and not a swap.
   *   3. Pin `X-Workspace-Id` to the target BEFORE re-deriving, so the ladder's
   *      own GET and the brand fetch both state which tenant they are for. If
   *      the server disagrees they 409 and we re-sync, rather than quietly
   *      returning somebody else's data.
   *   4. Re-derive workspace and brand together. `seatWorkspace` bumps the
   *      epoch as B lands, which is what re-runs every workspace-scoped effect
   *      and kills every in-flight poll.
   *
   * Errors are re-thrown for the caller to word — this file never toasts.
   */
  const switchWorkspace = useCallback(
    async (workspaceId) => {
      if (!workspaceId) return null;
      if (workspaceId === lastRealWsIdRef.current) return workspaceId;
      setWsSwitching(workspaceId);
      try {
        await api.post("/workspaces/switch", { workspace_id: workspaceId });
      } catch (e) {
        // Nothing local has changed yet — that is the point of doing this first.
        setWsSwitching(null);
        throw e;
      }
      clearBrandSeat();             // A's brand is not a "previous value" in B
      seatWorkspace(null);          // clears the header too
      setActiveWorkspaceId(workspaceId); // …then state the target explicitly
      setStatus(WS_STATUS.SETTLING);
      try {
        await Promise.all([runLadder(), refreshBrand()]);
      } finally {
        setWsSwitching(null);
      }
      return workspaceId;
    },
    [runLadder, refreshBrand, seatWorkspace, setStatus, clearBrandSeat]
  );

  const checkAuth = useCallback(async () => {
    try {
      const me = await api.get("/auth/me").then((r) => r.data);
      // If a DIFFERENT person is now signed in in this tab, drop anything the
      // previous one left in sessionStorage.
      //
      // logout() already clears these, but nothing forces a logout: user A can
      // simply walk away, and user B signs in over the live session. B then
      // lands on onboarding, has A's unconfirmed draft restored, and confirms
      // A's confidential brand into B's workspace. Real, reproduced.
      //
      // Keyed on the user CHANGING rather than patched into each of the five
      // login endpoints (/auth/session, /auth/login, /auth/verify-otp,
      // /auth/reset-password, /auth/signup) — a per-call-site fix silently fails
      // the day a sixth is added. sessionStorage is per-tab and survives reloads,
      // so a legitimate refresh keeps the same user's own draft.
      try {
        const prev = sessionStorage.getItem("lm_session_user");
        if (prev && me?.user_id && prev !== me.user_id) {
          // Every workspace's draft, not just the active one — the keys are
          // namespaced per workspace now (lib/brandDraft.js) and none of them
          // belong to whoever just signed in.
          clearAllBrandDrafts();
          // Same argument one level along: a brand PREFERENCE names a brand in
          // a workspace the new user may not even be a member of. Left behind,
          // it would be resolved against their list, miss, and be retired — but
          // on a shared machine it also leaks which brand the previous person
          // was working on into a stored key the new session reads.
          clearAllBrandPrefs();
          sessionStorage.removeItem("lm_skip_onboarding");
          sessionStorage.removeItem("lm_skip_draft_confirm");
        }
        if (me?.user_id) sessionStorage.setItem("lm_session_user", me.user_id);
      } catch (_) {}
      setUser(me);
      // WORKSPACE FIRST, THEN BRAND — and this is a fix, not a style choice.
      //
      // These used to run concurrently. `refreshBrand` resolves the stored
      // brand preference under `getActiveWorkspaceId()`, which is set by
      // `seatWorkspace` inside the ladder — so on a cold load the brand fetch
      // routinely won the race, read the preference under a null workspace id,
      // found nothing, and silently fell through to the deterministic default.
      // Symptom: pick brand B, reload, land on brand A, with the stored
      // preference sitting there correct and unread the whole time. Driven and
      // reproduced.
      //
      // Serialising also makes the `/brands` request carry the right
      // `X-Workspace-Id`. Concurrently it went out with none, and the server
      // fell back to the stored server-side preference — which happened to
      // agree, so the header did nothing here until it one day didn't.
      //
      // Cost is one round trip on cold load. The brand fetch genuinely depends
      // on knowing the workspace; pretending otherwise is what bought the bug.
      await runLadder();
      await refreshBrand();
    } catch (_) {
      setUser(null);
      seatWorkspace(null);
      clearBrandSeat();
      // Auth itself failed — ProtectedRoute redirects. Our banner must not
      // appear on top of a login redirect.
      authFailedRef.current = true;
      genRef.current++;
      clearTimers();
      setStatus(WS_STATUS.UNREACHABLE);
    } finally {
      setLoading(false);
    }
  }, [runLadder, refreshBrand, clearTimers, setStatus, seatWorkspace, clearBrandSeat]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // ── Self-heal §3.1 — background poll while unreachable ──────────────────
  useEffect(() => {
    if (workspaceStatus !== WS_STATUS.UNREACHABLE) return;
    if (authFailedRef.current) return;
    let cancelled = false;
    let id = null;

    const tick = () => {
      if (cancelled) return;
      const downFor = Date.now() - (unreachableAtRef.current || Date.now());

      // Owner decision 3: cap the poll after ~5 min on a HIDDEN tab. When we
      // stop, we say so — ws.down.watching.paused. Retract the promise before
      // the user discovers it lapsed.
      if (
        typeof document !== "undefined" &&
        document.hidden &&
        hiddenSinceRef.current &&
        Date.now() - hiddenSinceRef.current > HIDDEN_POLL_CAP_MS
      ) {
        setWatchStatus("paused");
        return; // no reschedule; visibilitychange resumes us
      }

      setWatchStatus("polling");
      healNudge("heal-poll");
      const interval = downFor > HEAL_BACKOFF_AFTER_MS ? HEAL_POLL_SLOW_MS : HEAL_POLL_MS;
      id = setTimeout(tick, interval);
    };

    id = setTimeout(tick, HEAL_POLL_MS);
    return () => {
      cancelled = true;
      if (id) clearTimeout(id);
    };
  }, [workspaceStatus, healNudge]);

  // ── Self-heal §3.2 visibilitychange, §3.3 online/offline ────────────────
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
        return;
      }
      hiddenSinceRef.current = 0;
      // The dominant mobile path. Returning to a stale `unreachable` screen
      // after a backgrounded minute is the worst possible experience — always
      // restart the ladder on visible, and never greet them with S4 cold.
      setWatchStatus("polling");
      healNudge("visibilitychange");
    };
    const onOnline = () => {
      setWsOffline(false);
      // ws.offline.body promises "We'll reconnect on our own the moment you're
      // back." THIS listener is what makes that sentence true. Honesty
      // condition 3 — without it, don't ship the string.
      healNudge("online");
    };
    const onOffline = () => setWsOffline(true);

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [healNudge]);

  // ── Self-heal §3.5 — any other 2xx proves the backend is up ─────────────
  useEffect(() => {
    return onApiSuccess((url) => {
      if (typeof url === "string" && url.includes("/workspaces/current")) return;
      const now = Date.now();
      if (now - lastNudgeRef.current < NUDGE_THROTTLE_MS) return;
      lastNudgeRef.current = now;
      healNudge("api-2xx");
    });
  }, [healNudge]);

  // ── Workspace reseat — the server moved us ──────────────────────────────
  // `X-Workspace-Reseated` says the workspace we thought we were in is gone
  // (removed from that team) and the server has already seated us elsewhere and
  // persisted it. Everything on screen is now labelled with a workspace the user
  // is not in, while every request reads and writes the new one. Re-derive both
  // workspace and brand from the server.
  //
  // Through the ladder, not around it: `refreshWorkspace` IS runLadder, so a
  // re-sync that lands mid-outage still gets the retries and still resolves to
  // `unreachable` rather than silently leaving the stale name up.
  //
  // `keepStatus`/`fresh: false` keep it silent — the workspace is already READY,
  // and flashing "Checking…" over a correction the user didn't ask for would be
  // noise. Whether they are TOLD their workspace changed is a copy decision that
  // belongs to a visible slot, not to this effect.
  //
  // The de-dupe and the no-loop guard live in lib/api.js, at the one place that
  // can see every response. This handler is deliberately dumb about both.
  useEffect(() => {
    return onWorkspaceReseated(async (reseatedFrom) => {
      // eslint-disable-next-line no-console
      console.warn("[workspace] reseated away from", reseatedFrom, "— re-syncing");
      clearBrandSeat(); // the old workspace's brand must not outlive the switch
      // Sequential for the reason spelled out in `checkAuth`: the brand
      // preference and the `/brands` request are both keyed on the ACTIVE
      // workspace id, and after a reseat that id is stale until the ladder
      // re-seats it. Resolving the brand first would read the preference of the
      // workspace the user was just moved OUT of.
      await runLadder({ fresh: false, keepStatus: true });
      await refreshBrand();
    });
  }, [runLadder, refreshBrand, clearBrandSeat]);

  // ── Workspace mismatch — the server refused our claim ───────────────────
  // We sent `X-Workspace-Id` naming a workspace this session cannot reach and
  // got a 409 back. Before Phase 5.8 that same request returned 200 with a
  // DIFFERENT tenant's data and nobody was any the wiser; the whole value of
  // the header is that this branch can exist at all.
  //
  // Drop the claim before re-deriving. The stale id must not ride along on the
  // re-sync's own requests, or the ladder 409s against itself — with no header
  // the server resolves the stored preference, which is by definition reachable.
  //
  // A FRESH ladder (not `keepStatus`) on purpose: `settling → loading → ready`
  // is the honest sequence when we genuinely do not know which tenant we are,
  // and it preserves the invariant that consumers read `workspaceStatus` rather
  // than `workspace === null`.
  //
  // De-dupe and the no-loop guard live in lib/api.js, next to the reseat ones.
  useEffect(() => {
    return onWorkspaceMismatch(async () => {
      clearBrandSeat();
      seatWorkspace(null);
      setStatus(WS_STATUS.SETTLING);
      // Sequential — same reason as `checkAuth`. Here it is starker: we have
      // just CLEARED the active workspace id, so a concurrent brand fetch would
      // resolve its preference under null every time.
      await runLadder();
      await refreshBrand();
    });
  }, [runLadder, refreshBrand, seatWorkspace, setStatus, clearBrandSeat]);

  // §3.4 route change — AuthProvider is mounted OUTSIDE BrowserRouter, so it
  // cannot use useLocation. AppShell calls this on pathname change instead.
  const routeChanged = useCallback(() => {
    const now = Date.now();
    if (now - lastRouteNudgeRef.current < ROUTE_NUDGE_THROTTLE_MS) return;
    lastRouteNudgeRef.current = now;
    healNudge("route-change");
  }, [healNudge]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const logout = useCallback(async () => {
    genRef.current++;
    clearTimers();
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setUser(null);
    seatWorkspace(null);
    clearBrandSeat();
    setStatus(WS_STATUS.SETTLING);
    try {
      sessionStorage.removeItem("lm_skip_onboarding");
      sessionStorage.removeItem("lm_skip_draft_confirm");
      // An unconfirmed brand draft must NOT outlive the session. On a shared
      // machine the next person to log in would otherwise have it restored
      // (Onboarding.jsx reads these on mount) and confirm it into THEIR
      // workspace. Clears every workspace's namespaced key — see lib/brandDraft.js.
      clearAllBrandDrafts();
      // Brand PREFERENCES are not secret the way a draft is, but they are still
      // this user's state and the next person on this machine should start from
      // their own default, not inherit a pointer into a workspace they may not
      // be in. Same lifetime rule as the drafts: the session ends, it goes.
      clearAllBrandPrefs();
    } catch (_) {}
    // The next person to sign in on this tab must not inherit an A→B epoch bump
    // from a session that is over.
    lastRealWsIdRef.current = null;
  }, [clearTimers, setStatus, seatWorkspace, clearBrandSeat]);

  return (
    <AuthCtx.Provider
      value={{
        user,
        workspace,
        brand,
        brands,
        brandLoading,
        brandGone,
        clearBrandGone,
        reportBrandGone,
        loading,
        workspaceStatus,
        tenantEpoch,
        // Alias, kept so every existing consumer keeps working unchanged while
        // call sites migrate. It is the SAME counter, so a consumer still
        // reading the old name now also re-runs on a brand change — which is
        // the behaviour every one of them wanted anyway.
        workspaceEpoch: tenantEpoch,
        wsSwitching,
        brandSwitching,
        wsAttempt,
        wsRequestOpen,
        wsNextRetryAt,
        wsRefCode,
        wsOffline,
        wsLaddersExhausted,
        wsHealing,
        watchStatus,
        wsAuthFailed: authFailedRef.current,
        retryWorkspace,
        healNudge,
        routeChanged,
        refreshAuth: checkAuth,
        refreshWorkspace,
        refreshBrand,
        switchWorkspace,
        switchBrand,
        logout,
        setUser,
        setWorkspace: seatWorkspace,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
